import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Col,
  Form,
  InputGroup,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';
import FalconComponentCard from 'components/common/FalconComponentCard';
import Flex from 'components/common/Flex';
import Pagination from 'components/common/Pagination';
import SoftBadge from 'components/common/SoftBadge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faUsers } from '@fortawesome/free-solid-svg-icons';
import corner6 from 'assets/img/illustrations/corner-6.png';
import _ from 'lodash';
import moment from 'moment';
import SimpleBarReact from 'simplebar-react';
import { toast } from 'react-toastify';
import {
  useInviteTenantUserMutation,
  useListTenantUsersQuery
} from 'rtk/org-admin/users.slice';
import AdminInviteUserModal from './AdminInviteUserModal';

const DEFAULT_PAGE_SIZE = 10;

function getActiveBadgeVariant(isActive) {
  return isActive ? 'success' : 'danger';
}

function formatRole(roleName) {
  if (!roleName) return '—';
  return String(roleName)
    .split('_')
    .filter(Boolean)
    .map(part => part.charAt(0) + part.slice(1).toLowerCase())
    .join(' ');
}

function getOrganizationInviteUrl(userGuid) {
  if (!userGuid) return '';
  const basePath = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${window.location.origin}${basePath}/signup/org/user?token=${userGuid}`;
}

const AdminTenantUsers = () => {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);

  const indexStart = pageIndex * pageSize;

  const {
    data: usersRes,
    isFetching,
    isLoading,
    isError: usersLoadError,
    refetch
  } = useListTenantUsersQuery({
    indexStart,
    maxResults: pageSize,
    search: searchTerm
  });

  const items = usersRes?.Objects || [];
  const totalRecords = usersRes?.TotalRecords || 0;
  const endOfResults = !!usersRes?.EndOfResults;
  const loading = isFetching || isLoading;
  const [inviteTenantUser, { isLoading: inviteSending }] =
    useInviteTenantUserMutation();

  const debouncedSetSearch = useMemo(() => {
    return _.debounce(value => {
      setSearchTerm(value);
      setPageIndex(0);
    }, 250);
  }, []);

  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [debouncedSetSearch]);

  useEffect(() => {
    setPageIndex(0);
  }, [pageSize]);

  const onSearchChange = e => {
    const value = e.target.value || '';
    setSearchInput(value);
    debouncedSetSearch(value);
  };

  const handleInviteUser = payload => {
    const email = payload?.Email?.trim();
    const firstName = payload?.FirstName?.trim();
    const lastName = payload?.LastName?.trim();
    const roleName = payload?.RoleName?.trim();

    if (!email || !firstName || !lastName || !roleName) {
      toast.warning('Please complete all required invite fields.', {
        theme: 'colored',
        position: 'bottom-center'
      });
      return;
    }

    inviteTenantUser({
      Email: email,
      FirstName: firstName,
      LastName: lastName,
      RoleName: roleName
    })
      .unwrap()
      .then(() => {
        toast.success('User invite sent successfully.', {
          theme: 'colored',
          position: 'bottom-center'
        });
        setShowInviteModal(false);
      })
      .catch(err => {
        toast.error(
          _.get(
            err,
            'Message',
            'Failed to send user invite. Please try again.'
          ),
          {
            theme: 'colored',
            position: 'bottom-center'
          }
        );
      });
  };

  const handleCopyInviteUrl = userGuid => {
    const inviteUrl = getOrganizationInviteUrl(userGuid);

    if (!inviteUrl) {
      toast.error('Unable to copy invite URL.', {
        theme: 'colored',
        position: 'bottom-center'
      });
      return;
    }

    navigator.clipboard
      .writeText(inviteUrl)
      .then(() => {
        toast.success('Invite URL copied to clipboard.', {
          theme: 'colored',
          position: 'bottom-center'
        });
      })
      .catch(() => {
        toast.error('Failed to copy invite URL.', {
          theme: 'colored',
          position: 'bottom-center'
        });
      });
  };

  return (
    <>
      <Row className="mb-3 g-3">
        <Col lg={12}>
          <FalconComponentCard noGuttersBottom>
            <FalconComponentCard.Header
              image={corner6}
              className="px-4"
              title="Organization Users"
              subtitle="View tenant users"
              endEl={
                <Flex className="g-2">
                  <div className="mx-1">
                    <InputGroup>
                      <Form.Control
                        type="search"
                        value={searchInput}
                        onChange={onSearchChange}
                        placeholder="Search users..."
                        name="search"
                      />
                      <InputGroup.Text className="bg-white">
                        <FontAwesomeIcon icon="search" />
                      </InputGroup.Text>
                    </InputGroup>
                  </div>
                  <div className="mx-1">
                    <Button
                      variant="primary"
                      onClick={() => setShowInviteModal(true)}
                    >
                      <FontAwesomeIcon icon={faPlus} className="me-2" />
                      Invite User
                    </Button>
                  </div>
                </Flex>
              }
            />

            <FalconComponentCard.Body noLight className="py-0 px-4">
              {isLoading && (
                <Flex justifyContent="center" className="py-3">
                  <Spinner animation="border" size="sm" />
                </Flex>
              )}

              {items.length > 0 && !isLoading && (
                <Pagination
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  totalRecords={totalRecords}
                  currentCount={items.length}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  endOfResults={endOfResults}
                  disabled={loading}
                  pageSizeOptions={[5, 10, 15, 25]}
                  onRefresh={() => refetch()}
                  loading={loading}
                />
              )}

              {items.length > 0 && !usersLoadError && !isLoading && (
                <SimpleBarReact>
                  <Table
                    striped
                    bordered
                    className={`fs--1 mb-0${
                      loading ? ' refreshing-skeleton' : ''
                    }`}
                  >
                    <thead className="bg-200 text-900 text-nowrap align-middle">
                      <tr>
                        <th scope="col">Name</th>
                        <th scope="col">Email</th>
                        <th scope="col">Role</th>
                        <th scope="col">Phone</th>
                        <th scope="col">Status</th>
                        <th scope="col">Created</th>
                        <th scope="col"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(user => {
                        const createdDate = user?.Created
                          ? moment.utc(user.Created).local().format('l, LT')
                          : '—';

                        return (
                          <tr
                            key={user?.GUID || user?.Email || createdDate}
                            className="align-middle"
                          >
                            <td className="align-middle py-2">
                              <div className="fw-semi-bold">
                                {user?.FullName ||
                                  user?.ContactInformation ||
                                  '—'}
                              </div>
                            </td>
                            <td className="align-middle py-2">
                              {user?.Email || '—'}
                            </td>
                            <td className="align-middle py-2">
                              {formatRole(user?.RoleName)}
                            </td>
                            <td className="align-middle py-2">
                              {user?.Telephone || '—'}
                            </td>
                            <td className="align-middle py-2">
                              <SoftBadge
                                bg={getActiveBadgeVariant(user?.Active)}
                                pill
                              >
                                {user?.Active ? 'Active' : 'Inactive'}
                              </SoftBadge>
                            </td>
                            <td className="align-middle py-2">{createdDate}</td>
                            <td className="align-middle py-2">
                              {!user?.Active && user?.GUID ? (
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() => handleCopyInviteUrl(user.GUID)}
                                >
                                  Copy Invite URL
                                </Button>
                              ) : (
                                '—'
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </SimpleBarReact>
              )}

              {items.length === 0 &&
                !loading &&
                (usersLoadError ? (
                  <div className="text-center py-5">
                    <FontAwesomeIcon icon={faUsers} size="3x" />
                    <p className="my-3 text-600 fs--1">
                      Unable to load users. Try again.
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <FontAwesomeIcon icon={faUsers} size="3x" />
                    <p className="my-3 text-600 fs--1">
                      No users found.
                      {searchTerm
                        ? ' Try adjusting your search.'
                        : ' Tenant users will appear here.'}
                    </p>
                  </div>
                ))}
            </FalconComponentCard.Body>
          </FalconComponentCard>
        </Col>
      </Row>

      <AdminInviteUserModal
        show={showInviteModal}
        onHide={() => setShowInviteModal(false)}
        onSubmit={handleInviteUser}
        sending={inviteSending}
      />
    </>
  );
};

export default AdminTenantUsers;
